import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { fileDraggerStyle } from './file-dragger-style';
import type {
  FileDraggerProps,
  FileDraggerState,
  UnstyledFileDraggerProps,
} from './types';

export class UnStyleFileDragger extends React.PureComponent<UnstyledFileDraggerProps, FileDraggerState> {
  static propTypes: React.WeakValidationMap<UnstyledFileDraggerProps> = {
    allowedFileExtension: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    onInvalidExtension: PropTypes.func.isRequired,
    onReadFile: PropTypes.func.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
      parseDecimal: PropTypes.func.isRequired,
    }).isRequired as PropTypes.Validator<FileDraggerProps['i18n']>,
    dragging: PropTypes.func,
    label: PropTypes.string,
    description: PropTypes.string,
    dropDescription: PropTypes.string,
    classes: PropTypes.shape(_.mapObject(
      fileDraggerStyle({}, {}),
      _.constant(PropTypes.string.isRequired)
    )).isRequired as PropTypes.Validator<UnstyledFileDraggerProps['classes']>,
    // eslint-disable-next-line react/forbid-prop-types
    fileButton: PropTypes.any,
    useLargeStyling: PropTypes.bool,
  };

  static defaultProps: Pick<
    UnstyledFileDraggerProps,
    'label' | 'description' | 'dropDescription' | 'dragging' | 'fileButton' | 'useLargeStyling'
  > = {
    label: '',
    description: '',
    dropDescription: '',
    dragging: () => {},
    fileButton: null,
    useLargeStyling: false,
  };

  constructor(props: UnstyledFileDraggerProps) {
    super(props);

    this.state = {
      isDraging: false,
    };
  }
  onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!this.state.isDraging) {
      this.handleDraging(true);
    }
  };

  onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const { onReadFile } = this.props;
    e.preventDefault();
    e.stopPropagation();

    const dragFiles = e.dataTransfer.files;
    const valideExtensionFile = [];

    for (let i = 0; i < dragFiles.length; i += 1) {
      const file = dragFiles[i];
      if (this.validateExtension(file)) {
        valideExtensionFile.push(file);
      }
    }

    if (valideExtensionFile.length > 0) {
      onReadFile(valideExtensionFile);
    }

    this.handleDraging(false);
  };

  onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.state.isDraging) {
      this.handleDraging(false);
    }
  };

  handleDraging = (isDraging: boolean) => {
    const { dragging } = this.props;
    if (dragging) {
      dragging(isDraging);
    }

    this.setState({
      isDraging,
    });
  };

  validateExtension = (file: File) => {
    const { allowedFileExtension, onInvalidExtension } = this.props;

    const extension = file.type.split('/').pop();
    const fileType = `.${extension}`;

    if (allowedFileExtension.indexOf(fileType) === -1) {
      onInvalidExtension();
      return false;
    }

    return true;
  };

  render() {
    const { isDraging } = this.state;
    const {
      label, description, dropDescription, classes, fileButton, useLargeStyling,
    } = this.props;

    const {
      fileSelectedWrapper,
      fileSelectedIcon,
      fileSelectedText,
      fileDraggerRoot,
      largeFileDraggerRoot,
      fileDefaultWrapper,
      largeFileDefaultWrapper,
      fileUploadIcon,
      fileUploadText,
      fileUploadTip,
      fileUploadButton,
      largeFileUploadButton,
      icon,
    } = classes;

    return (
      <div
        className={classnames(classes.fileDragWrapper, 'file-drag-wrapper')}
        aria-label={label}
        onDragOver={this.onDragOver}
        onDrop={this.onDrop}
        onDragLeave={this.onDragLeave}
      >
        {
          isDraging ?
            <div className={classnames(fileSelectedWrapper, 'file-dragger-wrapper')}>
              <div className={classnames(fileSelectedIcon, 'file-dragger-icon')}>
                {/* todo: upgrade to font Task 3945732 */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048" fill="#0078D4">
                  <path d="M960 0q133 0 255 34t230 96 194 150 150 195 97 229 34 256q0 133-34 255t-96 230-150 194-195 150-229 97-256 34q-133 0-255-34t-230-96-194-150-150-195-97-229T0 960q0-133 34-255t96-230 150-194 195-150 229-97T960 0zm0 1792q115 0 221-30t198-84 169-130 130-168 84-199 30-221q0-115-30-221t-84-198-130-169-168-130-199-84-221-30q-115 0-221 30t-198 84-169 130-130 168-84 199-30 221q0 115 30 221t84 198 130 169 168 130 199 84 221 30zm0-1311l430 434-92 90-274-278v681H896V727l-274 278-92-90 430-434z" />
                </svg>
              </div>
              { dropDescription &&
                <div className={classnames(fileSelectedText, 'file-dragger-text')}>{dropDescription}</div>
              }
            </div>
          :
            <div className={classnames(useLargeStyling ? largeFileDraggerRoot : fileDraggerRoot, 'file-dragger')}>
              <div className={classnames(useLargeStyling ? largeFileDefaultWrapper : fileDefaultWrapper, 'file-default-wrapper')}>
                <div className={classnames(fileUploadIcon, 'file-upload-icon')}>
                  <span className={classnames('iconba', 'iconba-OpenFile', icon)} />
                </div>
                { label &&
                  <div className={classnames(fileUploadText, 'file-upload-text')}>{label}</div>
                }
                { description &&
                  <div className={classnames(fileUploadTip, 'file-upload-tip')}>{description}</div>
                }
              </div>
              {
                fileButton && (
                  <div className={classnames(useLargeStyling ? largeFileUploadButton : fileUploadButton, 'file-upload-button')}>
                    {fileButton}
                  </div>
                )
              }
            </div>
        }
      </div>
    );
  }
}

export const FileDragger: React.FC<FileDraggerProps> = withDefaultStyles(UnStyleFileDragger, fileDraggerStyle);
